import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['overlay', 'nav', 'purposeSubmenu', 'count', 'certification', 'icon']

  connect() {
    this.lastScrollTop = 0
    this.isHidden = false
    this.handleScroll = this.handleScroll.bind(this)

    this.alignMonkeyImg = this.alignMonkeyImg.bind(this);
    window.addEventListener("resize", this.alignMonkeyImg);

    this.scrollElement = document.querySelector("#container") 
    if (this.scrollElement) {
      this.scrollElement.addEventListener("scroll", this.handleScroll)
    }

    this.closePurposeOnLoad = this.closePurposeOnLoad.bind(this)
    document.addEventListener("turbo:load", this.closePurposeOnLoad)
    this.forceCloseMenu()
  }

  disconnect() {
    if (this.scrollElement) {
      this.scrollElement.removeEventListener("scroll", this.handleScroll)
    }
    window.removeEventListener("resize", this.alignMonkeyImg);
    document.removeEventListener("turbo:load", this.closePurposeOnLoad)
    document.removeEventListener("turbo:render", this.forceCloseMenu)
  }

  handleScroll() {
    const currentScroll = this.scrollElement.scrollTop || 0
    const delta = 10

    const goingDown = currentScroll > this.lastScrollTop + delta
    const goingUp = currentScroll < this.lastScrollTop - delta

    if (goingDown && currentScroll > 100 && !this.isHidden) {
      this.element.classList.add("is-hidden")
      this.isHidden = true
    }

    if (goingUp && this.isHidden) {
      this.element.classList.remove("is-hidden")
      this.isHidden = false
    }

    if (currentScroll <= 0) {
      this.element.classList.remove("is-hidden")
      this.isHidden = false
    }

    this.lastScrollTop = currentScroll
  }

  // Forces the main menu to close (used when navigating back)
  forceCloseMenu() {
    if (this.hasOverlayTarget && this.overlayTarget.classList.contains('open')) {
      this.overlayTarget.classList.remove('open')
      this.navTarget?.classList.remove('menu-open')
    }
  }

  toggle() {
    this.overlayTarget.classList.toggle('open');
    if (this.hasNavTarget) this.navTarget.classList.toggle('menu-open');

    const isMenuOpen = this.overlayTarget.classList.contains('open');

    if (isMenuOpen) {
      requestAnimationFrame(() => requestAnimationFrame(() => this.alignMonkeyImg()));
      if (document.fonts?.ready) document.fonts.ready.then(() => this.alignMonkeyImg());
    }

    if (!isMenuOpen && this.hasPurposeSubmenuTarget) {
      this.purposeSubmenuTarget.classList.remove('visible');
      const button = this.element.querySelector('[data-action="click->header#togglePurpose"]');
      if (button) button.setAttribute('aria-expanded','false');
    }
  }


  // Ensures the purpose submenu is closed on page load
  closePurposeOnLoad() {
    if (this.hasPurposeSubmenuTarget) {
      this.purposeSubmenuTarget.classList.remove("visible")

      const button = this.element.querySelector('[data-action="click->header#togglePurpose"]')
      if (button) {
        button.setAttribute("aria-expanded", "false")
      }
    }
  }

  togglePurpose(event) {
    event.stopPropagation();

    if (this.hasPurposeSubmenuTarget) {
      const button = event.currentTarget;
      const isVisible = this.purposeSubmenuTarget.classList.contains('visible');

      this.purposeSubmenuTarget.classList.toggle('visible', !isVisible);
      button.setAttribute('aria-expanded', String(!isVisible));
    }
  }

  // Centers the "monkey" icon with the certification logos by measuring their vertical center and setting icon.style.top.
  alignMonkeyImg() {
    if (!(this.hasOverlayTarget && this.hasIconTarget && this.hasCertificationTarget)) return;

    const { top: oTop, height: overlayHeight } = this.overlayTarget.getBoundingClientRect();

    const logos = [...this.certificationTarget.querySelectorAll('a')].map(el => el.getBoundingClientRect());
    const { top: logosTop, bottom: logosBottom } = logos.length
      ? { top: Math.min(...logos.map(r => r.top)), bottom: Math.max(...logos.map(r => r.bottom)) }
      : this.certificationTarget.getBoundingClientRect();

    this.iconTarget.style.top = '0px';
    const { height: iconHeight } = this.iconTarget.getBoundingClientRect();

    const nudge  = parseFloat(getComputedStyle(this.iconTarget).getPropertyValue('--monkey-nudge')) || 0;
    const margin = 16;
    const top    = ((logosTop + logosBottom) / 2) - oTop - (iconHeight / 2) + nudge;
    const clamp  = Math.min(overlayHeight - iconHeight - margin, Math.max(margin, top));

    this.iconTarget.style.top = `${Math.round(clamp)}px`;
  }

  openCart() {
    const event = new CustomEvent('modal:open', { detail: {size: 'small'}})
    document.dispatchEvent(event)

    Turbo.visit('/cart', { frame: 'active-modal-content' })
  }

  showSearches() {
    const event = new CustomEvent('modal:open', { detail: {size: 'small'}})
    document.dispatchEvent(event)
    Turbo.visit("/my_searches", { frame: "active-modal-content" })
  }

  openLogin(e) {
    e.preventDefault()

    // Check for meaningful return URL - not empty string and not login pages
    const dataReturnTo = e.currentTarget.dataset.returnTo
    const hasDataReturnTo = dataReturnTo && dataReturnTo.trim() !== ''
    const returnTo = hasDataReturnTo ? dataReturnTo : location.href

    // Only set sessionStorage if we have a meaningful return URL (not from checkout login)
    if (hasDataReturnTo) {
      sessionStorage.setItem('after_login_url', returnTo)
    } else {
      sessionStorage.removeItem('after_login_url')
    }

    document.dispatchEvent(new CustomEvent('modal:open', { detail: { size: 'small' }}))

    // Only include return_to parameter if we have a meaningful return URL
    const returnToParam = hasDataReturnTo ?
      `?return_to=${encodeURIComponent(returnTo)}` :
      ''

    // Add checkout flag when no specific return URL is wanted
    const checkoutParam = hasDataReturnTo ? '' : (returnToParam ? '&' : '?') + 'from_checkout=true'

    fetch(`/login${returnToParam}${checkoutParam}`, {
      headers: { Accept: 'text/vnd.turbo-stream.html' }
    })
      .then(r => r.text())
      .then(Turbo.renderStreamMessage)
  }


}